// Implémentation de la matrice de cellules sous la forme d'une map (level) de maps (baselineKey) de maps (scenarioCode)
// => contient une AkmRow

import { WidgetCell } from "../../vo/workingdata/widget-line.obj";
import { IBaseline, SisterResult } from "../../model/interfaces";

// stockée en mémoire
export class CellMap {

    // la matrice des sisterResult
    // par level
    // par scenar
    // par measureFullKey|agg
    // par sisterKey = SisterResult
    private sisterResultMap: Map<number, Map<string, Map<string , Map<string, SisterResult>>>>  = new Map();
    
    /**
     * retourne l'instance UNIQUE ou la crée
     * @param wc 
     */
    public getSisterResultInstance(sr: SisterResult): SisterResult {
        let result: SisterResult = this.sisterResultMap.get(sr.l)?.get(sr.sc)?.get(sr.magg)?.get(sr.sk);
        if(!result) {
            result = this.addSisterResultInstance(sr);
       
        }
        return result;
    }

    /**
     * la widgetCell n'est pas encore présente dans la map, on l'ajoute
     * @param wc 
     */
    private addSisterResultInstance(sr: SisterResult): SisterResult {
        if(!this.sisterResultMap.has(sr.l)) {
            this.sisterResultMap.set(sr.l, new Map());
        }
        if(!this.sisterResultMap.get(sr.l).has(sr.sc)) {
            this.sisterResultMap.get(sr.l).set(sr.sc, new Map());
        }
        if(!this.sisterResultMap.get(sr.l).get(sr.sc).has(sr.magg)) {
            this.sisterResultMap.get(sr.l).get(sr.sc).set(sr.magg, new Map());
        }
        this.sisterResultMap.get(sr.l).get(sr.sc).get(sr.magg).set(sr.sk, sr);
        return sr;
    }




    // la matrice des cellules. Seulement utilisé pour les dépendances
    // par level
    // par scenar
    // par baseline.key
    // par mesure#code = widgetCell
    private cellMap: Map<number, Map<string, Map<string , Map<string, WidgetCell>>>>  = new Map();

    /**
     * retourne l'instance UNIQUE pour cette cellule, ou la crée. Permet de stocker les children & les sisters sous forme simple
     * @param wc 
     */
    public getCellInstance(wc: WidgetCell): WidgetCell {
        let result: WidgetCell = this.cellMap.get(wc.l)?.get(wc.sc)?.get(wc.k)?.get(wc.m);
        if(!result) {
            result = this.addCellInstance(wc);
        }
        return result;
    }

    /**
     * la widgetCell n'est pas encore présente dans la map, on l'ajoute
     * @param wc 
     */
    private addCellInstance(wc: WidgetCell): WidgetCell {
        if(!this.cellMap.has(wc.l)) {
            this.cellMap.set(wc.l, new Map());
        }
        if(!this.cellMap.get(wc.l).has(wc.sc)) {
            this.cellMap.get(wc.l).set(wc.sc, new Map());
        }
        if(!this.cellMap.get(wc.l).get(wc.sc).has(wc.k)) {
            this.cellMap.get(wc.l).get(wc.sc).set(wc.k, new Map());
        }
        this.cellMap.get(wc.l).get(wc.sc).get(wc.k).set(wc.m, wc);
        return wc;
    }


    /**
     * la map de cellules 
     * par stepLevel
     * => par scenar
     * => par blKey
     * contient une AkmRow
     *  => contient une map de AkmCell
     * 
     * Pour les calculs de simulation, le scenar est FAKE
     *                   level       scenar       blKey
     */
    public rowMap : Map<number, Map<string, Map<string, IBaseline>>> = new Map();

    // rinçage
    public clear(): void {
      this.rowMap = new Map();
      this.cellMap = new Map();
    }

    /**
     * Suppression d'une baseline
     * 
     * @param stepLevel du step
     * @param blKey de la baseline
     * @param scenario cible
     */
    public deleteRow(stepLevel: number, blKey:string, scenario: string) {
        this.rowMap.get(stepLevel)?.get(scenario)?.delete(blKey);
    }

    /**
     * Collecte d'une baseline
     * @param stepLevel du step
     * @param blKey de la baseline
     * @param scenario cible
     * @param createIfEmpty faut-il créer la structure si inexistante ?
     * @returns la ligne (une baseline)
     */
    public getRow(stepLevel: number, blKey:string, scenario: string, createIfEmpty: boolean = false): IBaseline {
        
        let row: IBaseline = this.rowMap.get(stepLevel)?.get(scenario)?.get(blKey) || null;
        
        if (createIfEmpty && !row) { // demande de création

            row = {l: stepLevel, sc: scenario, k: blKey, vs: {}}; // construction de la ligne

            if(!this.rowMap.has(stepLevel)) {
                this.rowMap.set(stepLevel, new Map());
            }
            if(!this.rowMap.get(stepLevel).has(scenario)) {
                this.rowMap.get(stepLevel).set(scenario, new Map());
            }
            if(!this.rowMap.get(stepLevel).get(scenario).has(blKey)) {
                this.rowMap.get(stepLevel).get(scenario).set(blKey, row);
            }
        }
        return row;
    }

    /**
     * Ajout d'une baseline dans la map
     * @param stepLevel le level du step
     * @param blKey la clé de la baseline
     * @param scenario le scénario
     * @param row la baseline
     */
    public addRow(stepLevel: number, blKey:string, scenario: string, row:IBaseline) {

        if(!this.rowMap.has(stepLevel)) {
            this.rowMap.set(stepLevel, new Map());
        }
        if(!this.rowMap.get(stepLevel).has(scenario)) {
            this.rowMap.get(stepLevel).set(scenario, new Map());
        }
        this.rowMap.get(stepLevel).get(scenario).set(blKey, row);

    }

    /**
     * Collecte sous la forme d'une map des baselines pour un level et un scenario donné.
     * Si le level et/ou le scenario ne sont pas fournis, on collecte tous les levels ou tous les scénarios
     * @param stepLevel le level du step (non obligatoire)
     * @param scenario le scénario (non obligatoire)
     * @returns la map des baselines
     */
    public getAllRows(stepLevel?: number, scenario?: string): Map<number, Map<string, Map<string, IBaseline>>> {
        if (!stepLevel && !scenario) {
            return this.rowMap;
        }

        const resultMap: Map<number, Map<string, Map<string, IBaseline>>> = new Map();

        if (stepLevel) {
            const stepLevelMap:Map<string, Map<string, IBaseline>> = this.rowMap.get(stepLevel);
            if (!scenario) {
                resultMap.set(stepLevel, stepLevelMap);
            } else {
                const resultStepLevelMap: Map<string, Map<string, IBaseline>> = new Map();
                resultStepLevelMap.set(scenario, stepLevelMap?.get(scenario));
                resultMap.set(stepLevel, resultStepLevelMap);
            }
        } else {
            this.rowMap.forEach((stepLevelMap, stepLevel)=> {
                const resultStepLevelMap: Map<string, Map<string, IBaseline>> = new Map();
                resultStepLevelMap.set(scenario, stepLevelMap?.get(scenario));
                resultMap.set(stepLevel, resultStepLevelMap);   
            })
        }

        return resultMap;
    }

    /**
     * Collecte sous la forme d'une map des baselines pour un level et un scenario donné.
     * @param stepLevel le level du step 
     * @param scenario le scénario
     * @returns la map des baselines
     */
    public getRows(stepLevel: number, scenario: string): Map<string, IBaseline> {
        return this.rowMap.get(stepLevel)?.get(scenario);
    }
}